import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Box,
  SimpleGrid,
  PopoverAnchor,
} from "@chakra-ui/react";
import { round } from "lodash";
import { useMemo } from "react";

import { Suggestion, Word } from "utils/types";

interface CollocateItemProps {
  base: string;
  data: Word;
  onSubmit: (value: Suggestion) => void;
  showInfo?: boolean;
}

const CollocateItem = ({
  base,
  data,
  onSubmit,
  showInfo = true,
}: CollocateItemProps) => {
  const leftPercentage = useMemo(
    () => round(data.left_percentage * 100, 2),
    [data.left_percentage]
  );

  const rightPercentage = useMemo(
    () => round(100 - leftPercentage, 2),
    [leftPercentage]
  );
  const leftHigher = leftPercentage > rightPercentage;
  const leftSideWord = leftHigher
    ? `${base}...${data.collocate} (${leftPercentage}%)`
    : `${data.collocate}...${base} (${rightPercentage}%)`;
  const rightSideWord = leftHigher
    ? `${data.collocate}...${base} (${rightPercentage}%)`
    : `${base}...${data.collocate} (${leftPercentage}%)`;
  const leftPopup = leftHigher
    ? `"${base}" appears before "${data.collocate}" ${leftPercentage}% of the time.`
    : `"${base}" appears after "${data.collocate}" ${rightPercentage}% of the time.`;

  return (
    <Flex
      alignItems="center"
      mb={{ base: "1", md: "4" }}
      flexDir={{ base: "column", md: "row" }}
    >
      <Box
        display="inline-flex"
        flex="1"
        color="primary"
        onClick={() =>
          onSubmit({ word: data.collocate, pos: data.collocate_pos })
        }
        cursor="pointer"
      >
        <Text>{data.collocate}</Text>
        {!!data.collocate_pos && (
          <Text fontSize="0.6rem">{data.collocate_pos}</Text>
        )}
      </Box>
      <Flex flex="4" alignItems="center" w={{ base: "100%", md: "auto" }}>
        {!data.match_filters ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius="sm"
            height="2.5rem"
            borderWidth="1px"
            borderColor="grayTxt"
            w="full"
          >
            <Text color="grayTxt" fontSize="sm">
              Not Found
            </Text>
          </Box>
        ) : (
          <Popover placement="top">
            <SimpleGrid columns={2} w="full" position="relative">
              <Box
                display="flex"
                bg="primary"
                justifyContent="center"
                alignItems="center"
                borderLeftRadius="sm"
                height="2.5rem"
              >
                <PopoverTrigger>
                  <PopoverAnchor>
                    <Text
                      color="white"
                      fontSize="sm"
                      opacity="0.8"
                      wordBreak="break-word"
                    >
                      {leftSideWord}
                    </Text>
                  </PopoverAnchor>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverBody color="grayTxt">{leftPopup}</PopoverBody>
                </PopoverContent>
              </Box>
              <Box
                display="flex"
                bg="second"
                justifyContent="center"
                alignItems="center"
                borderRightRadius="sm"
                height="2.5rem"
              >
                <PopoverTrigger>
                  <Box>
                    <Text
                      color="white"
                      fontSize="sm"
                      opacity="0.8"
                      wordBreak="break-word"
                    >
                      {rightSideWord}
                    </Text>
                    {!!showInfo && (
                      <Flex
                        position="absolute"
                        right="1rem"
                        top="0rem"
                        color="white"
                        height="100%"
                        cursor="pointer"
                        alignItems="center"
                      >
                        <InfoOutlineIcon />
                      </Flex>
                    )}
                  </Box>
                </PopoverTrigger>
              </Box>
            </SimpleGrid>
          </Popover>
        )}
      </Flex>
    </Flex>
  );
};

export default CollocateItem;
